import { yupResolver } from "@hookform/resolvers/yup";
import { Box, type DrawerProps, Grid, IconButton, Typography } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { Drawer, DrawerContent } from "Components/Analyses/_Shared/Drawer";
import { Button } from "Components/_Shared/Buttons/Button";
import { Flex } from "Components/_Shared/Core/Flex";
import { DialogModal } from "Components/_Shared/DialogModal";
import { ReactComponent as CloseIcon } from "Components/_Shared/Icons/Assets/close.svg";
import { BlFormInput } from "Forms/Components/BlFormInput";
import { FormCharacterCounter } from "Forms/Components/FormCharacterCounter";
import { FormDropdown, type Option } from "Forms/Components/FormDropdown";
import { StyledLabelAdornment } from "Forms/Components/FormStyles";
import { type ImmovableAssetItemFormType, getImmovableAssetItemFormSchema } from "Forms/Schemas/Assets/immovableAssetItemFormSchema";
import {
  getGetAssetsOverviewQueryKey,
  getListImmovableAssetItemsQueryKey,
  useCreateImmovableAssetItem,
  useDeleteAssetItem,
  useUpdateImmovableAssetItem,
} from "Infrastructure/ApiZfp/AnalyzerApi";
import { AmountFrequency, type ImmovableAssetItem, ImmovableAssetItemImmovableSubType } from "Infrastructure/ApiZfp/Schema";
import { Spacing } from "Infrastructure/Themes/Themes";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { IMMOVABLE_SUBTYPE_RESOURCE_KEY } from "Infrastructure/Utils/AssetUtils";
import { type FC, useContext, useEffect, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { toast } from "sonner";
import { AnalysisDetailContext } from "../Detail/AnalysisDetailContext";
import { NOTE_MAX_LENGTH } from "../constants";

type Props = {
  readonly onClose: () => void;
  readonly immovableAsset?: ImmovableAssetItem;
} & DrawerProps;

const HOUSE_TYPES: string[] = [ImmovableAssetItemImmovableSubType.house, ImmovableAssetItemImmovableSubType.cabin];
const AREA_TYPES: string[] = [
  ImmovableAssetItemImmovableSubType.apartment,
  ImmovableAssetItemImmovableSubType.land,
  ImmovableAssetItemImmovableSubType.forest,
  ImmovableAssetItemImmovableSubType.other,
];
const HOUSEHOLD_INSURANCE_TYPES: string[] = [
  ImmovableAssetItemImmovableSubType.house,
  ImmovableAssetItemImmovableSubType.cabin,
  ImmovableAssetItemImmovableSubType.apartment,
];

export const ImmovableAssetItemDrawer: FC<Props> = ({ onClose, immovableAsset, open, ...rest }) => {
  const { t } = useResource();
  const queryClient = useQueryClient();
  const { readOnly, analysisId, versionId } = useContext(AnalysisDetailContext);

  const [deleteModalOpen, setDeleteModalOpen] = useState(false);

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: getGetAssetsOverviewQueryKey(analysisId, versionId) });
    queryClient.invalidateQueries({ queryKey: getListImmovableAssetItemsQueryKey(analysisId, versionId) });
  };

  const { mutate: create, isPending: isCreating } = useCreateImmovableAssetItem();
  const { mutate: deleteItem, isPending: isDeleting } = useDeleteAssetItem();
  const { mutate: update, isPending: isUpdating } = useUpdateImmovableAssetItem();

  const isMutating = isCreating || isUpdating || isDeleting;

  const immovableAssets: Option[] = (Object.keys(ImmovableAssetItemImmovableSubType) as Array<ImmovableAssetItemImmovableSubType>).map(
    (value) => ({
      value,
      name: t(Resources.AnalysisDetail.Assets.Immovable.ImmovableAssetType[IMMOVABLE_SUBTYPE_RESOURCE_KEY[value]]),
    }),
  );

  const methods = useForm<ImmovableAssetItemFormType>({
    resolver: yupResolver(getImmovableAssetItemFormSchema(t)),
    mode: "onBlur",
  });

  const {
    control,
    formState: { errors },
    handleSubmit,
    reset,
    watch,
  } = methods;

  useEffect(() => {
    if (open) {
      const defaultValues = {
        immovableAssetType: immovableAsset?.immovableSubType,
        name: immovableAsset?.name,
        value: immovableAsset?.amount.value,
        note: immovableAsset?.note ?? undefined,
        propertyInsuranceAmount: immovableAsset?.propertyInsurance?.value,
        householdInsuranceAmount: immovableAsset?.householdInsurance?.value,
        basementArea: immovableAsset?.basementArea ?? undefined,
        floorCount: immovableAsset?.floors ?? 0,
        firstFloorArea: immovableAsset?.firstFloorArea ?? undefined,
        secondFloorArea: immovableAsset?.secondFloorArea ?? undefined,
        thirdFloorArea: immovableAsset?.thirdFloorsArea ?? undefined,
        fourthFloorArea: immovableAsset?.fourthFloorArea ?? undefined,
        otherFloorArea: immovableAsset?.otherFloorsArea ?? undefined,
        area: immovableAsset?.area ?? undefined,
      };
      reset(defaultValues);
      return;
    }

    reset({});
  }, [immovableAsset, open, reset]);

  const immovableAssetType = watch("immovableAssetType");
  const floorCount = watch("floorCount") ?? 0;

  const isHouseType = HOUSE_TYPES.includes(immovableAssetType);
  const isAreaType = AREA_TYPES.includes(immovableAssetType);
  const hasHouseholdInsurance = HOUSEHOLD_INSURANCE_TYPES.includes(immovableAssetType);

  const onSubmit = (data: ImmovableAssetItemFormType) => {
    try {
      const payload = {
        amount: {
          currencyCode: "CZK",
          frequency: AmountFrequency.one_time,
          value: data.value,
        },
        note: data.note || "",
        name: data.name,
        immovableSubType: data.immovableAssetType as ImmovableAssetItemImmovableSubType,
        isCoveredByInsurance: !!immovableAsset?.isCoveredByInsurance,
        isUnderFinancing: !!immovableAsset?.isUnderFinancing,
        isUsableForGoals: !!immovableAsset?.isUsableForGoals,
        propertyInsurance: data.propertyInsuranceAmount
          ? { value: data.propertyInsuranceAmount, currencyCode: "CZK", frequency: AmountFrequency.one_time }
          : undefined,
        householdInsurance:
          hasHouseholdInsurance && data.householdInsuranceAmount
            ? { value: data.householdInsuranceAmount, currencyCode: "CZK", frequency: AmountFrequency.one_time }
            : undefined,
        basementArea: isHouseType ? (data.basementArea ?? undefined) : undefined,
        floors: isHouseType ? (data.floorCount ?? undefined) : undefined,
        firstFloorArea: isHouseType && floorCount > 0 ? (data.firstFloorArea ?? undefined) : undefined,
        secondFloorArea: isHouseType && floorCount > 1 ? (data.secondFloorArea ?? undefined) : undefined,
        thirdFloorsArea: isHouseType && floorCount > 2 ? (data.thirdFloorArea ?? undefined) : undefined,
        fourthFloorArea: isHouseType && floorCount > 3 ? (data.fourthFloorArea ?? undefined) : undefined,
        otherFloorsArea: isHouseType && floorCount > 4 ? (data.otherFloorArea ?? undefined) : undefined,
        area: isAreaType ? (data.area ?? undefined) : undefined,
      };

      if (!immovableAsset) {
        create(
          { analysisId, versionId, data: payload },
          {
            onSuccess: () => {
              toast.success(t(Resources.AnalysisDetail.Assets.Immovable.Created));
              invalidate();
              onClose();
            },
            onError: () => toast.error(t(Resources.AnalysisDetail.Assets.Immovable.Error)),
          },
        );
      } else {
        update(
          { analysisId, versionId, itemId: immovableAsset.guid, data: payload },
          {
            onSuccess: () => {
              toast.success(t(Resources.AnalysisDetail.Assets.Immovable.Edited));
              invalidate();
              onClose();
            },
            onError: () => toast.error(t(Resources.AnalysisDetail.Assets.Immovable.Error)),
          },
        );
      }
    } catch {
      toast.error(t(Resources.AnalysisDetail.Assets.Immovable.Error));
    }
  };

  const onDelete = () => {
    try {
      deleteItem(
        { analysisId, versionId, itemId: immovableAsset?.guid || "" },
        {
          onSuccess: () => {
            toast.success(t(Resources.AnalysisDetail.Assets.Immovable.Deleted));
            invalidate();
            setDeleteModalOpen(false);
            onClose();
          },
          onError: () => toast.error(t(Resources.AnalysisDetail.Assets.Immovable.Error)),
        },
      );
    } catch {
      toast.error(t(Resources.AnalysisDetail.Assets.Immovable.Error));
    }
  };

  return (
    <>
      <Drawer anchor="right" open={open} {...rest} onClose={onClose}>
        <FormProvider {...methods}>
          <Box component="form" display="flex" flexDirection="column" height="100%" onSubmit={handleSubmit(onSubmit)}>
            <Flex flexShrink={0} justifyContent="space-between" alignItems="center" p={Spacing.XXL}>
              <Typography variant="h2">
                {immovableAsset ? t(Resources.AnalysisDetail.Assets.Immovable.Edit) : t(Resources.AnalysisDetail.Assets.Immovable.Add)}
              </Typography>

              <IconButton onClick={onClose}>
                <CloseIcon />
              </IconButton>
            </Flex>
            <DrawerContent sx={{ flex: 1, minHeight: 0 }}>
              <Box p={Spacing.XXL}>
                <Grid container spacing={Spacing.XL}>
                  <Grid size={4}>
                    <FormDropdown
                      name="immovableAssetType"
                      options={immovableAssets}
                      control={control}
                      disabled
                      labelEndAdornment={<StyledLabelAdornment />}
                      label={t(Resources.AnalysisDetail.Assets.Immovable.ImmovableAssetType.Title)}
                      errors={errors}
                    />
                  </Grid>
                  <Grid size={8}>
                    <BlFormInput
                      control={control}
                      disabled={readOnly}
                      name="name"
                      label={t(Resources.AnalysisDetail.AssetsAndContracts.Form.Name)}
                      labelEndAdornment={<StyledLabelAdornment />}
                      errors={errors}
                    />
                  </Grid>
                  <Grid size={12}>
                    <BlFormInput
                      control={control}
                      disabled
                      name="value"
                      label={t(Resources.AnalysisDetail.Assets.Value)}
                      labelEndAdornment={<StyledLabelAdornment />}
                      errors={errors}
                      unmask="typed"
                      placeholder="0"
                      mask={[
                        {
                          mask: Number,
                          thousandsSeparator: " ",
                          scale: 0,
                          min: 0,
                        },
                      ]}
                      inputEndAdornment={
                        <Typography variant="body2" color="secondary">
                          {t(Resources.Common.CZKSuffix)}
                        </Typography>
                      }
                    />
                  </Grid>

                  <Grid size={isHouseType ? 6 : isAreaType && hasHouseholdInsurance ? 4 : 6}>
                    <BlFormInput
                      disabled={readOnly}
                      control={control}
                      name="propertyInsuranceAmount"
                      label={t(Resources.AnalysisDetail.AssetsAndContracts.Form.PropertyInsuranceAmount)}
                      errors={errors}
                      mask={[{ mask: Number, thousandsSeparator: " ", scale: 0, min: 0 }]}
                      inputEndAdornment={
                        <Typography variant="body2" color="secondary">
                          {t(Resources.Common.CZKSuffix)}
                        </Typography>
                      }
                    />
                  </Grid>
                  {hasHouseholdInsurance && (
                    <Grid size={isHouseType ? 6 : 4}>
                      <BlFormInput
                        disabled={readOnly}
                        control={control}
                        name="householdInsuranceAmount"
                        label={t(Resources.AnalysisDetail.AssetsAndContracts.Form.HouseholdInsuranceAmount)}
                        errors={errors}
                        mask={[{ mask: Number, thousandsSeparator: " ", scale: 0, min: 0 }]}
                        inputEndAdornment={
                          <Typography variant="body2" color="secondary">
                            {t(Resources.Common.CZKSuffix)}
                          </Typography>
                        }
                      />
                    </Grid>
                  )}
                  {isAreaType && (
                    <Grid size={hasHouseholdInsurance ? 4 : 6}>
                      <BlFormInput
                        disabled={readOnly}
                        control={control}
                        name="area"
                        label={t(Resources.AnalysisDetail.AssetsAndContracts.Form.Area)}
                        errors={errors}
                        inputEndAdornment={
                          <Typography variant="body2" color="secondary">
                            m<sup>2</sup>
                          </Typography>
                        }
                        mask={[{ mask: Number, thousandsSeparator: " ", scale: 0, min: 0 }]}
                      />
                    </Grid>
                  )}

                  {isHouseType && (
                    <>
                      <Grid size={4}>
                        <BlFormInput
                          disabled={readOnly}
                          control={control}
                          name="basementArea"
                          label={t(Resources.AnalysisDetail.AssetsAndContracts.Form.BasementArea)}
                          errors={errors}
                          inputEndAdornment={
                            <Typography variant="body2" color="secondary">
                              m<sup>2</sup>
                            </Typography>
                          }
                          mask={[{ mask: Number, thousandsSeparator: " ", scale: 0, min: 0 }]}
                        />
                      </Grid>
                      <Grid size={4}>
                        <FormDropdown
                          name="floorCount"
                          options={[
                            { name: "0", value: "0" },
                            { name: "1", value: "1" },
                            { name: "2", value: "2" },
                            { name: "3", value: "3" },
                            { name: "4", value: "4" },
                            { name: t(Resources.Common.Other), value: "5" },
                          ]}
                          control={control}
                          disabled={readOnly}
                          label={t(Resources.AnalysisDetail.AssetsAndContracts.Form.FloorCount)}
                          errors={errors}
                        />
                      </Grid>
                      {floorCount > 0 && (
                        <Grid size={4}>
                          <BlFormInput
                            disabled={readOnly}
                            control={control}
                            name="firstFloorArea"
                            label={t(Resources.AnalysisDetail.AssetsAndContracts.Form.NthFloorArea, { count: 1 })}
                            errors={errors}
                            inputEndAdornment={
                              <Typography variant="body2" color="secondary">
                                m<sup>2</sup>
                              </Typography>
                            }
                            mask={[{ mask: Number, thousandsSeparator: " ", scale: 0, min: 0 }]}
                          />
                        </Grid>
                      )}
                      {floorCount > 1 && (
                        <Grid size={4}>
                          <BlFormInput
                            disabled={readOnly}
                            control={control}
                            name="secondFloorArea"
                            label={t(Resources.AnalysisDetail.AssetsAndContracts.Form.NthFloorArea, { count: 2 })}
                            errors={errors}
                            inputEndAdornment={
                              <Typography variant="body2" color="secondary">
                                m<sup>2</sup>
                              </Typography>
                            }
                            mask={[{ mask: Number, thousandsSeparator: " ", scale: 0, min: 0 }]}
                          />
                        </Grid>
                      )}
                      {floorCount > 2 && (
                        <Grid size={4}>
                          <BlFormInput
                            disabled={readOnly}
                            control={control}
                            name="thirdFloorArea"
                            label={t(Resources.AnalysisDetail.AssetsAndContracts.Form.NthFloorArea, { count: 3 })}
                            errors={errors}
                            inputEndAdornment={
                              <Typography variant="body2" color="secondary">
                                m<sup>2</sup>
                              </Typography>
                            }
                            mask={[{ mask: Number, thousandsSeparator: " ", scale: 0, min: 0 }]}
                          />
                        </Grid>
                      )}
                      {floorCount > 3 && (
                        <Grid size={4}>
                          <BlFormInput
                            disabled={readOnly}
                            control={control}
                            name="fourthFloorArea"
                            label={t(Resources.AnalysisDetail.AssetsAndContracts.Form.NthFloorArea, { count: 4 })}
                            errors={errors}
                            inputEndAdornment={
                              <Typography variant="body2" color="secondary">
                                m<sup>2</sup>
                              </Typography>
                            }
                            mask={[{ mask: Number, thousandsSeparator: " ", scale: 0, min: 0 }]}
                          />
                        </Grid>
                      )}
                      {floorCount > 4 && (
                        <Grid size={4}>
                          <BlFormInput
                            disabled={readOnly}
                            control={control}
                            name="otherFloorArea"
                            label={t(Resources.AnalysisDetail.AssetsAndContracts.Form.OtherFloorArea)}
                            errors={errors}
                            inputEndAdornment={
                              <Typography variant="body2" color="secondary">
                                m<sup>2</sup>
                              </Typography>
                            }
                            mask={[{ mask: Number, thousandsSeparator: " ", scale: 0, min: 0 }]}
                          />
                        </Grid>
                      )}
                    </>
                  )}

                  <Grid size={12}>
                    <BlFormInput
                      disabled={readOnly}
                      control={control}
                      label={t(Resources.Common.Note)}
                      name="note"
                      errors={errors}
                      multiline
                      minRows={6}
                      maxRows={8}
                      characterCounter={<FormCharacterCounter characterLimit={NOTE_MAX_LENGTH} name={"note"} />}
                    />
                  </Grid>
                </Grid>
              </Box>
            </DrawerContent>
            <Flex flexShrink={0} alignItems="center" justifyContent="space-between" py={Spacing.XXL} px={Spacing.XXL}>
              <Box>
                {!!immovableAsset && (
                  <Button
                    variant="text"
                    color="error"
                    disabled={readOnly || isMutating}
                    sx={{
                      marginRight: Spacing.XL,
                    }}
                    onClick={() => setDeleteModalOpen(true)}
                  >
                    {t(Resources.Common.Delete)}
                  </Button>
                )}
              </Box>
              <Flex flexDirection="row" justifyContent="flex-end">
                <Button
                  onClick={onClose}
                  color="secondary"
                  sx={{
                    marginRight: Spacing.XL,
                  }}
                >
                  {t(Resources.Common.Close)}
                </Button>
                <Button disabled={readOnly || isMutating} type="submit">
                  {immovableAsset ? t(Resources.Common.Edit) : t(Resources.Common.Create)}
                </Button>
              </Flex>
            </Flex>
          </Box>
        </FormProvider>
      </Drawer>
      <DialogModal
        isOpen={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        title={t(Resources.AnalysisDetail.AssetsAndContracts.Form.DeleteAssetTitle)}
        subtitle={t(Resources.AnalysisDetail.AssetsAndContracts.Form.DeleteAssetText)}
        primaryAction={
          <Button disabled={readOnly || isMutating} onClick={onDelete}>
            {t(Resources.Common.Yes)}
          </Button>
        }
      />
    </>
  );
};
